// ---------------------------------------------------------------------------
// Bounded Flex — constraint utilities for the bounded flex layout system.
//
// Provides maxChildren enforcement, proportional size redistribution,
// and equal distribution across flex children.
// ---------------------------------------------------------------------------

import { componentRegistry } from '@/features/page-editor/registry/registry'

/** Default maxChildren per container type */
const MAX_CHILDREN_DEFAULTS: Record<string, number> = {
  container: 8,
  row: 6,
  grid: 12,
  card: 6,
  section: 8,
  sidebar: 3,
  form: 10,
  tabs: 6,
  accordion: 8,
  carousel: 8,
  collapsible: 4,
  'dropdown-menu': 8,
  dialog: 6,
  popover: 4,
  tooltip: 2,
}

/**
 * Get maxChildren for a component type.
 * Checks registry permissions first, then falls back to built-in defaults.
 */
export function getMaxChildren(type: string): number | undefined {
  const def = componentRegistry.get(type)
  if (def?.permissions?.maxChildren != null) {
    return def.permissions.maxChildren
  }
  return MAX_CHILDREN_DEFAULTS[type]
}

/**
 * Check if a child can be added to a parent of the given type.
 */
export function canAddChild(parentType: string, currentCount: number): boolean {
  const max = getMaxChildren(parentType)
  if (max == null) return true
  return currentCount < max
}

/**
 * Redistribute child sizes (percentages) when one child is resized.
 * Ensures all sizes sum to 100 and no size goes below minSize.
 *
 * @param sizes - current percentage sizes of each child
 * @param changedIndex - index of the child being resized
 * @param newSize - new percentage for the changed child
 * @param minSize - minimum percentage per child (default 10)
 * @returns new array of percentages summing to 100
 */
export function redistributeSizes(
  sizes: number[],
  changedIndex: number,
  newSize: number,
  minSize: number = 10,
): number[] {
  if (sizes.length <= 1) return [100]

  const result = [...sizes]

  // Clamp newSize so the changed child can't exceed 100 - (siblings * minSize)
  const maxAllowed = 100 - (sizes.length - 1) * minSize
  const clamped = Math.max(minSize, Math.min(maxAllowed, newSize))
  result[changedIndex] = clamped

  // Remaining space for siblings
  const remaining = 100 - clamped
  const othersTotal = sizes.reduce((sum, s, i) => (i === changedIndex ? sum : sum + s), 0)

  if (othersTotal === 0) {
    // Edge case: all siblings were 0 — distribute equally
    const perSibling = remaining / (sizes.length - 1)
    for (let i = 0; i < result.length; i++) {
      if (i !== changedIndex) result[i] = perSibling
    }
  } else {
    // Distribute proportionally based on current ratios
    for (let i = 0; i < result.length; i++) {
      if (i === changedIndex) continue
      result[i] = (sizes[i] / othersTotal) * remaining
    }
  }

  // Enforce minSize and redistribute any overflow
  let deficit = 0
  let flexibleCount = 0
  for (let i = 0; i < result.length; i++) {
    if (i === changedIndex) continue
    if (result[i] < minSize) {
      deficit += minSize - result[i]
      result[i] = minSize
    } else {
      flexibleCount++
    }
  }
  if (deficit > 0 && flexibleCount > 0) {
    for (let i = 0; i < result.length; i++) {
      if (i === changedIndex || result[i] <= minSize) continue
      result[i] -= deficit / flexibleCount
    }
  }

  // Final rounding: round all values, adjust last one for exact sum = 100
  const rounded = result.map((v) => Math.round(v * 100) / 100)
  const roundedSum = rounded.reduce((a, b) => a + b, 0)
  const diff = 100 - roundedSum
  if (diff !== 0) {
    // Add diff to the last non-changed sibling
    for (let i = rounded.length - 1; i >= 0; i--) {
      if (i !== changedIndex) {
        rounded[i] = Math.round((rounded[i] + diff) * 100) / 100
        break
      }
    }
  }

  return rounded
}

/**
 * Distribute sizes equally among N children, summing to 100.
 */
export function equalDistribute(count: number): number[] {
  if (count <= 0) return []
  if (count === 1) return [100]

  const base = Math.floor((100 / count) * 100) / 100
  const result = Array(count).fill(base)
  // Assign remainder to the last child
  const sum = base * count
  result[count - 1] = Math.round((base + (100 - sum)) * 100) / 100
  return result
}
